import React, { useState, useEffect } from 'react';
import { getClients, getShifts, saveShift, deleteShift, getShiftNoteByShift, saveShiftNote } from '../utils/storageUtils';
import { searchNDISItems, calculateShiftTotal } from '../utils/ndisUtils';
import { format } from 'date-fns';

function Shifts() {
  const [shifts, setShifts] = useState([]);
  const [clients, setClients] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingShift, setEditingShift] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [recentlyUsedServices, setRecentlyUsedServices] = useState([]);
  const [showDeleteId, setShowDeleteId] = useState(null);
  const [noteModalShift, setNoteModalShift] = useState(null);
  const [noteModalText, setNoteModalText] = useState('');
  const [noteModalExisting, setNoteModalExisting] = useState(null);
  const [expandedShiftId, setExpandedShiftId] = useState(null);

  const toggleExpandShift = (shiftId) => {
    if (expandedShiftId === shiftId) {
      setExpandedShiftId(null);
      setNoteModalText('');
    } else {
      const existing = getShiftNoteByShift(shiftId);
      setNoteModalText(existing ? existing.text : '');
      setExpandedShiftId(shiftId);
    }
  };
  
  const [formData, setFormData] = useState({
    clientId: '',
    date: format(new Date(), 'yyyy-MM-dd'),
    startTime: '09:00',
    endTime: '17:00',
    hours: 0,
    ndisItemCode: '',
    serviceDescription: '',
    hourlyRate: '',
    kilometers: 0,
    notes: '',
    status: 'completed'
  });

  useEffect(() => {
    loadShifts();
    loadClients();
    loadRecentlyUsedServices();
  }, []);

  useEffect(() => {
    // Auto-calculate hours when times change
    if (formData.startTime && formData.endTime) {
      const [startHour, startMin] = formData.startTime.split(':').map(Number);
      const [endHour, endMin] = formData.endTime.split(':').map(Number);
      
      const startMinutes = startHour * 60 + startMin;
      const endMinutes = endHour * 60 + endMin;
      const diffMinutes = endMinutes - startMinutes;
      
      if (diffMinutes > 0) {
        const hours = (diffMinutes / 60).toFixed(2);
        setFormData(prev => ({ ...prev, hours: parseFloat(hours) }));
      }
    }
  }, [formData.startTime, formData.endTime]);

  const loadShifts = () => {
    const shiftsData = getShifts();
    setShifts(shiftsData.sort((a, b) => new Date(b.date) - new Date(a.date)));
  };

  const loadClients = () => {
    const clientsData = getClients();
    setClients(clientsData.filter(c => c.status === 'active'));
  };

  const loadRecentlyUsedServices = () => {
    try {
      const recent = localStorage.getItem('recentlyUsedServices');
      if (recent) {
        setRecentlyUsedServices(JSON.parse(recent));
      }
    } catch (error) {
      // Error handled silently
    }
  };

  const saveToRecentlyUsed = (item) => {
    try {
      let recent = [...recentlyUsedServices];
      
      // Remove if already exists
      recent = recent.filter(service => service.item_code !== item.item_code);
      
      // Add to beginning
      recent.unshift(item);
      
      // Keep only last 3
      recent = recent.slice(0, 3);
      
      setRecentlyUsedServices(recent);
      localStorage.setItem('recentlyUsedServices', JSON.stringify(recent));
    } catch (error) {
      // Error handled silently
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleServiceSearch = (query) => {
    setSearchQuery(query);
    if (query.length >= 2) {
      const results = searchNDISItems(query);
      setSearchResults(results.slice(0, 10)); // Limit to 10 results
      setShowSearchResults(true);
    } else {
      setSearchResults([]);
      setShowSearchResults(false);
    }
  };

  const handleSearchFocus = () => {
    // Show results when field is focused, even if already filled
    if (searchQuery.length >= 2) {
      const results = searchNDISItems(searchQuery);
      setSearchResults(results.slice(0, 10));
      setShowSearchResults(true);
    }
  };

  const selectNDISItem = (item) => {
    setFormData(prev => ({
      ...prev,
      ndisItemCode: item.item_code,
      serviceDescription: item.description,
      hourlyRate: item.national_price || ''
    }));
    setSearchQuery(item.description);
    setShowSearchResults(false);
    saveToRecentlyUsed(item);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Calculate totals
    const costs = calculateShiftTotal(
      parseFloat(formData.hours) || 0,
      parseFloat(formData.hourlyRate) || 0,
      parseFloat(formData.kilometers) || 0
    );
    
    const shiftToSave = {
      ...formData,
      hours: parseFloat(formData.hours) || 0,
      hourlyRate: parseFloat(formData.hourlyRate) || 0,
      kilometers: parseFloat(formData.kilometers) || 0,
      serviceTotal: costs.serviceTotal,
      transportTotal: costs.transportTotal,
      total: costs.total,
      id: editingShift ? editingShift.id : undefined,
      updatedAt: new Date().toISOString()
    };
    
    saveShift(shiftToSave);
    resetForm();
    loadShifts();
  };

  const resetForm = () => {
    setFormData({
      clientId: '',
      date: format(new Date(), 'yyyy-MM-dd'),
      startTime: '09:00',
      endTime: '17:00',
      hours: 0,
      ndisItemCode: '',
      serviceDescription: '',
      hourlyRate: '',
      kilometers: 0,
      notes: '',
      status: 'completed'
    });
    setSearchQuery('');
    setEditingShift(null);
    setShowForm(false);
  };

  const handleEdit = (shift) => {
    setFormData(shift);
    setSearchQuery(shift.serviceDescription);
    setEditingShift(shift);
    setShowForm(true);
  };

  const handleDelete = (shiftId) => {
    if (window.confirm('Are you sure you want to delete this shift?')) {
      deleteShift(shiftId);
      loadShifts();
    }
  };

  const getClientName = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    return client ? client.name : 'Unknown Client';
  };

  // Helper function to move day/time keywords to the beginning of service description
  const formatServiceDescription = (description) => {
    const keywords = [
      'Weekday Night',
      'Weekday Evening', 
      'Weekday Daytime',
      'Saturday',
      'Sunday',
      'Public Holiday',
      'Night-Time Sleepover'
    ];
    
    // Find if any keyword exists in the description
    for (const keyword of keywords) {
      const regex = new RegExp(`\\b${keyword}\\b`, 'i');
      const match = description.match(regex);
      if (match) {
        // Remove the keyword from its current position
        const cleanedDescription = description.replace(regex, '').trim();
        // Add it to the beginning
        return `${match[0]} - ${cleanedDescription}`;
      }
    }
    
    // If no keyword found, return original description
    return description;
  };

  return (
    <div className="shifts-page">
      <div className="page-header">
        <h1>Shifts</h1>
        <button 
          className="btn btn-primary" 
          onClick={() => setShowForm(!showForm)}
        >
          {showForm ? 'Cancel' : '+ Add Shift'}
        </button>
      </div>

      {showForm && (
        <div className="shift-form-container">
          <h2>{editingShift ? 'Edit Shift' : 'Add New Shift'}</h2>
          <form onSubmit={handleSubmit} className="shift-form">
            <div className="form-group">
              <label>Client *</label>
              <select
                name="clientId"
                value={formData.clientId}
                onChange={handleInputChange}
                required
              >
                <option value="">Select a client</option>
                {clients.map(client => (
                  <option key={client.id} value={client.id}>
                    {client.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Date *</label>
                <input
                  type="date"
                  name="date"
                  value={formData.date}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>Start Time</label>
                <input
                  type="time"
                  name="startTime"
                  value={formData.startTime}
                  onChange={handleInputChange}
                />
              </div>
              <div className="form-group">
                <label>End Time</label>
                <input
                  type="time"
                  name="endTime"
                  value={formData.endTime}
                  onChange={handleInputChange}
                />
              </div>
            </div>

            <div className="form-group">
              <label>Hours Worked *</label>
              <input
                type="number"
                name="hours"
                value={formData.hours}
                onChange={handleInputChange}
                step="0.25"
                min="0"
                required
              />
              <small>Automatically calculated from start/end time</small>
            </div>

            <div className="form-group service-search">
              <label>Search NDIS Service *</label>
              
              {recentlyUsedServices.length > 0 && (
                <div className="recently-used-services">
                  <span className="recently-used-label">Recently Used:</span>
                  {recentlyUsedServices.map(service => (
                    <button
                      key={service.item_code}
                      type="button"
                      className="btn-recently-used"
                      onClick={() => selectNDISItem(service)}
                    >
                      {formatServiceDescription(service.description)} (${service.national_price})
                    </button>
                  ))}
                </div>
              )}
              
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => handleServiceSearch(e.target.value)}
                onFocus={handleSearchFocus}
                placeholder="Search by service type, day, time... (e.g., 'community saturday')"
              />
              {showSearchResults && searchResults.length > 0 && (
                <div className="search-results">
                  {searchResults.map(item => (
                    <div 
                      key={item.item_code} 
                      className="search-result-item"
                      onClick={() => selectNDISItem(item)}
                    >
                      <div className="result-code">{item.item_code}</div>
                      <div className="result-description">{item.description}</div>
                      <div className="result-price">${item.national_price}/{item.unit}</div>
                    </div>
                  ))}
                </div>
              )}
              {showSearchResults && searchResults.length === 0 && searchQuery.length >= 2 && (
                <div className="search-results">
                  <div className="no-results">No matching services found</div>
                </div>
              )}
            </div>

            <div className="form-group">
              <label>NDIS Item Code *</label>
              <input
                type="text"
                name="ndisItemCode"
                value={formData.ndisItemCode}
                onChange={handleInputChange}
                required
                readOnly
              />
            </div>

            <div className="form-group">
              <label>Service Description *</label>
              <textarea
                name="serviceDescription"
                value={formData.serviceDescription}
                onChange={handleInputChange}
                rows="2"
                required
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Hourly Rate *</label>
                <input
                  type="number"
                  name="hourlyRate"
                  value={formData.hourlyRate}
                  onChange={handleInputChange}
                  step="0.01"
                  min="0"
                  required
                />
              </div>
              <div className="form-group">
                <label>Kilometers Driven</label>
                <input
                  type="number"
                  name="kilometers"
                  value={formData.kilometers}
                  onChange={handleInputChange}
                  step="0.1"
                  min="0"
                />
              </div>
            </div>

            <div className="form-group">
              <label>Notes</label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                rows="2"
                placeholder="Any additional notes about this shift"
              />
            </div>

            <div className="form-group">
              <label>Status</label>
              <select
                name="status"
                value={formData.status}
                onChange={handleInputChange}
              >
                <option value="scheduled">Scheduled</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>

            {formData.hours > 0 && formData.hourlyRate > 0 && (
              <div className="shift-summary">
                <h3>Shift Summary</h3>
                <div className="summary-row">
                  <span>Service ({formData.hours}h × ${formData.hourlyRate}):</span>
                  <span>${(formData.hours * formData.hourlyRate).toFixed(2)}</span>
                </div>
                {parseFloat(formData.kilometers || 0) > 0 && (
                  <div className="summary-row">
                    <span>Transport ({parseFloat(formData.kilometers || 0).toFixed(1)}km × $1.00):</span>
                    <span>${parseFloat(formData.kilometers || 0).toFixed(2)}</span>
                  </div>
                )}
                <div className="summary-row total">
                  <span>Total:</span>
                  <span>${calculateShiftTotal(
                    parseFloat(formData.hours) || 0,
                    parseFloat(formData.hourlyRate) || 0,
                    parseFloat(formData.kilometers) || 0
                  ).total}</span>
                </div>
              </div>
            )}

            <div className="form-actions">
              <button type="submit" className="btn btn-primary">
                {editingShift ? 'Update Shift' : 'Add Shift'}
              </button>
              <button type="button" className="btn btn-secondary" onClick={resetForm}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="shifts-list">
        {shifts.length === 0 ? (
          <div className="empty-state">
            <p>No shifts recorded yet. Add your first shift to get started!</p>
          </div>
        ) : (
          <div className="shifts-table">
            <table>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Client</th>
                  <th>Service</th>
                  <th>Hours</th>
                  <th>Rate</th>
                  <th>KM</th>
                  <th>Total</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {shifts.map(shift => (
                  <React.Fragment key={shift.id}>
                    <tr 
                      className={`shift-row ${expandedShiftId === shift.id ? 'shift-row-expanded' : ''}`}
                    >
                      <td 
                        data-label="Date" 
                        className="shift-row-clickable"
                        onClick={() => toggleExpandShift(shift.id)}
                      >
                        {format(new Date(shift.date), 'dd/MM/yyyy')}
                      </td>
                      <td 
                        data-label="Client"
                        className="shift-row-clickable"
                        onClick={() => toggleExpandShift(shift.id)}
                      >
                        {getClientName(shift.clientId)}
                      </td>
                      <td 
                        data-label="Service"
                        className="shift-row-clickable"
                        onClick={() => toggleExpandShift(shift.id)}
                      >
                        <div className="service-cell">
                          <small>{shift.ndisItemCode}</small>
                          <div>{shift.serviceDescription}</div>
                        </div>
                      </td>
                      <td 
                        data-label="Hours"
                        className="shift-row-clickable"
                        onClick={() => toggleExpandShift(shift.id)}
                      >
                        {shift.hours}h
                      </td>
                      <td 
                        data-label="Rate"
                        className="shift-row-clickable"
                        onClick={() => toggleExpandShift(shift.id)}
                      >
                        ${shift.hourlyRate}
                      </td>
                      <td 
                        data-label="KM"
                        className="shift-row-clickable"
                        onClick={() => toggleExpandShift(shift.id)}
                      >
                        {shift.kilometers}km
                      </td>
                      <td 
                        data-label="Total" 
                        className="amount shift-row-clickable"
                        onClick={() => toggleExpandShift(shift.id)}
                      >
                        ${shift.total?.toFixed(2)}
                      </td>
                      <td 
                        data-label="Status"
                        className="shift-row-clickable"
                        onClick={() => toggleExpandShift(shift.id)}
                      >
                        <span className={`status-badge ${shift.status}`}>
                          {shift.status}
                        </span>
                      </td>
                      <td data-label="Actions" className="shift-actions-cell">
                        {/* Ellipsis + Edit — on mobile these float on top border */}
                        <div className="shift-actions-inline">
                          <div className="shift-delete-wrap">
                            <button 
                              className="btn-dots"
                              onClick={() => setShowDeleteId(showDeleteId === shift.id ? null : shift.id)}
                              title="More options"
                            >
                              ⋯
                            </button>
                            {showDeleteId === shift.id && (
                              <button
                                className="btn btn-small btn-danger shift-delete-popup"
                                onClick={() => { handleDelete(shift.id); setShowDeleteId(null); }}
                              >
                                Delete Shift
                              </button>
                            )}
                          </div>
                          <button 
                            className="btn btn-small btn-secondary" 
                            onClick={() => handleEdit(shift)}
                          >
                            Edit
                          </button>
                        </div>
                        {/* Five dots — on mobile these sit at bottom of card */}
                        <div className="shift-dots-indicator" onClick={() => toggleExpandShift(shift.id)}>
                          <span className="shift-dots">•••••</span>
                        </div>
                        {/* Expandable notes panel */}
                        <div className={`shift-notes-panel ${expandedShiftId === shift.id ? 'shift-notes-panel-open' : ''}`}>
                          <div className="shift-notes-content">
                            <div className="shift-notes-title">
                              Shift Note — {format(new Date(shift.date), 'dd/MM/yyyy')} ({shift.hours}h)
                            </div>
                            <textarea
                              className="shift-notes-textarea"
                              placeholder="Write your shift notes here... (e.g., what activities were done, client mood, any incidents)"
                              value={expandedShiftId === shift.id ? (noteModalText || '') : ''}
                              onChange={(e) => setNoteModalText(e.target.value)}
                              onClick={(e) => e.stopPropagation()}
                            />
                            <div className="shift-notes-buttons">
                              <button
                                className="btn btn-success"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (!noteModalText.trim()) return;
                                  const existing = getShiftNoteByShift(shift.id);
                                  const note = {
                                    ...(existing || {}),
                                    shiftId: shift.id,
                                    clientId: shift.clientId,
                                    shiftDate: shift.date,
                                    text: noteModalText.trim(),
                                  };
                                  saveShiftNote(note);
                                  setExpandedShiftId(null);
                                  setNoteModalText('');
                                  loadShifts();
                                }}
                              >
                                {getShiftNoteByShift(shift.id) ? 'Update Note' : 'Save Note'}
                              </button>
                              <button
                                className="btn btn-secondary"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setExpandedShiftId(null);
                                  setNoteModalText('');
                                }}
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Shift Note Modal */}
      {noteModalShift && (
        <div className="modal-overlay" onClick={() => { setNoteModalShift(null); setNoteModalText(''); setNoteModalExisting(null); }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <button
              className="btn-close"
              onClick={() => { setNoteModalShift(null); setNoteModalText(''); setNoteModalExisting(null); }}
              style={{ position: 'absolute', top: '1rem', right: '1rem' }}
            >
              ×
            </button>
            <h2 style={{ marginBottom: 'var(--space-md)' }}>
              Shift Note — {format(new Date(noteModalShift.date), 'dd/MM/yyyy')} ({noteModalShift.hours}h)
            </h2>
            <p style={{ color: 'var(--color-text-secondary)', marginBottom: 'var(--space-md)',
              fontSize: '0.9rem' }}>
              {noteModalShift.serviceDescription || noteModalShift.ndisItemCode} • Internal use only
            </p>
            <textarea
              value={noteModalText}
              onChange={(e) => setNoteModalText(e.target.value)}
              placeholder="Write your shift notes here... (e.g., what activities were done, client mood, any incidents)"
              style={{
                width: '100%',
                minHeight: '180px',
                padding: 'var(--space-md)',
                borderRadius: 'var(--radius-md)',
                border: '1px solid var(--color-border)',
                fontFamily: 'inherit',
                fontSize: '0.95rem',
                resize: 'vertical',
                marginBottom: 'var(--space-lg)',
              }}
            />
            <div style={{ display: 'flex', gap: 'var(--space-sm)' }}>
              <button
                className="btn btn-success"
                onClick={() => {
                  if (!noteModalText.trim()) return;
                  const clients = getClients();
                  const client = clients.find(c => c.id === noteModalShift.clientId);
                  const note = {
                    ...(noteModalExisting || {}),
                    shiftId: noteModalShift.id,
                    clientId: noteModalShift.clientId,
                    shiftDate: noteModalShift.date,
                    text: noteModalText.trim(),
                  };
                  saveShiftNote(note);
                  setNoteModalShift(null);
                  setNoteModalText('');
                  setNoteModalExisting(null);
                  loadShifts();
                }}
              >
                {noteModalExisting ? 'Update Note' : 'Save Note'}
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => { setNoteModalShift(null); setNoteModalText(''); setNoteModalExisting(null); }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Shifts;
